import * as uamActionStatusTypes from '../../consts/uam/uamActionStatusTypes';
import * as cmMessageProperties from '../../consts/communicationLayer/cmMessageProperties';
import {updateDeviceData} from '../../state/slices/iotData';
import {UNPLUGGED} from '../../consts/communicationLayer/cmHolderStatusTypes';
import {setIsHolderConnected} from '../../state/slices/mwDevice';
import {getUamFlowTypes} from '../../consts/uam/uamWebSocketActionTypes';
import uamFlowService from '../uam/uamFlowService';
import {selectIsHolderAssetId} from '../../state/selectors/uamData';
import {log} from '../log';
import uamClientService from '../uam/uamClientService';
import {setUamData} from '../../state/slices/uamData';
import * as cmMessageTypes from '../../consts/communicationLayer/cmMessageTypes';
import cmMessageRequestService from '../communicationLayer/cmMessageRequestService';
import {MwError} from '../errors/MwError';
import httpStatusTypesService from '../server/httpStatusTypesService';
import errorService from '../errors/errorService';
import {MW_ERRORS} from '../../consts/app/mwErrors';

export const getIotDeviceData = async ({uamTokenData, isHolder}) => {
    if (!isUamTokenDataValid({uamTokenData})) {
        throw errorService.createError(MW_ERRORS.ERROR_WRONG_DATA, 'Uam token data is not provided');
    }

    const {assetId} = uamTokenData;

    if (!assetId) throw errorService.createError(MW_ERRORS.ERROR_IOT_NO_ASSET_ID, 'AssetId is not provided');

    try {
        const deviceInfo = await uamClientService.getUamInfo(isHolder, uamTokenData);

        if (deviceInfo) return deviceInfo;
    } catch (e) {
        throw getCustomIotError(e);
    }

    throw errorService.createError(MW_ERRORS.ERROR_IOT_NO_ASSET_INFO, 'Device Info is not valid');
};

const isUamTokenDataValid = ({uamTokenData, onError}) => {
    const {uamApiUrl: apiUrl, uamApiKey: apiKey, uamAuthToken: authToken} = uamTokenData || {};

    if (!apiUrl || !apiKey || !authToken) {
        if (onError) {
            onError('Api data are not specified');
            return false;
        }

        throw new errorService.createError(MW_ERRORS.ERROR_WRONG_DATA, 'Uam token data is not provided');
    }

    return true;
};

export const updateUamTokenData = ({uamTokenData, onError, isHolder}) => {
    if (!isUamTokenDataValid({uamTokenData, onError})) return;

    const {uamApiUrl: apiUrl, uamApiKey: apiKey, uamAuthToken: authToken} = uamTokenData || {};

    setUamData({apiUrl, apiKey, authToken, isHolder});
};

const proceedAction = ({status, requestId, assetId, action}) => {
    const isPending = status === uamActionStatusTypes.PENDING;

    if (isPending) return;

    const isHolder = selectIsHolderAssetId(assetId);
    const flowType = getUamFlowTypes(action);
    const lastRequestId = uamFlowService.getFlow(flowType, isHolder)?.requestId;

    if (requestId !== lastRequestId) {
        log.info(`Answer for requestId ${requestId} is ignored. Waiting for ${lastRequestId}. isHolder: ${!!isHolder}`);
        return false;
    }

    const isFailed = status === uamActionStatusTypes.FAILED;
    const isSuccess = status === uamActionStatusTypes.SUCCESS;
    const isCanceled = status === uamActionStatusTypes.CANCELED;

    if (!isFailed && !isSuccess && !isCanceled) return;

    uamFlowService.clearFlow(flowType, isHolder);

    return status;
};

const updateDeviceCharacteristics = async (event) => {
    const details = event?.details;

    if (!details) return;

    switch (event.eventType) {
        case cmMessageProperties.SYSTEM_STATUS:
            //for USB inside details itself
            let systemStatus = {systemStatus: {...details}};
            let {holderState} = details;
            const {holderPlugged, holderConnection, characteristics} = details;
            const holderPluggedIn = holderPlugged || holderConnection === 'CONNECTED';

            if (holderState === undefined) {
                //for BLE inside characteristics.chargerSystemStatus node
                const {chargerSystemStatus} = characteristics || {};
                const {holderState: systemHolderState} = chargerSystemStatus || {};

                systemStatus = {systemStatus: {...chargerSystemStatus}};
                holderState = systemHolderState;
            }

            const isHolderConnected = holderPluggedIn && holderState !== UNPLUGGED;

            updateDeviceData(false, {device: systemStatus});
            setIsHolderConnected(isHolderConnected);
            break;
        case cmMessageProperties.BATTERY_STATUS:
            const {
                batteryChargeUnified,
                chargerBatteryTemperature: batteryTemperature,
                chargerBatteryVoltage: batteryVoltage,
            } = details;
            //to send in same way as assets api method
            const deviceData = {device: {batteryInfo: {batteryChargeUnified, batteryTemperature, batteryVoltage}}};

            updateDeviceData(false, deviceData);
            break;
        default:
            break;
    }
};

const publishResponseEvent = (data) => {
    const {frames, type, cyclicId, request_id, token, iterationNumber} = data || {};
    const isCmResponse =
        cyclicId &&
        frames &&
        (type === cmMessageTypes.STATUS_CHARACTERISTICS || type === cmMessageTypes.CYCLE_REQUESTS);

    if (!isCmResponse) return;

    const frame = frames[0];

    if (cyclicId === cmMessageProperties.CYCLE_FRAME) {
        cmMessageRequestService.publishCycleCharacteristicEvent({frame, request_id, token, iterationNumber});
    } else {
        cmMessageRequestService.publishCharacteristicEvent({[cyclicId]: {frame}});
    }
};

const getCustomIotError = (error, defaultErrorCode = MW_ERRORS.ERROR_IOT_CONNECTION_GENERAL) => {
    //send error if already MW_ERRORS type
    if (error instanceof MwError) return error;

    const statusCode = error.response?.status;
    const isNotAuthorized = httpStatusTypesService.isNotAuthorized(statusCode);

    if (isNotAuthorized) return errorService.createError(MW_ERRORS.ERROR_IOT_401, 'Unauthorized');

    return errorService.createError(defaultErrorCode, error.message);
};

export default {
    proceedAction,
    updateDeviceCharacteristics,
    publishResponseEvent,
    getCustomIotError,
};
